import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, PlayCircle, CheckCircle, HelpCircle, BookOpen, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useOnboarding } from '../../context/OnboardingContext';
import { getToursByRole, TOURS } from './TourData';

const HelpCenter = ({ userRole }) => {
  const { showHelpCenter, setShowHelpCenter, startTour, isTourCompleted, resetTours } = useOnboarding();
  
  const availableTours = getToursByRole(userRole);

  const quickTips = userRole === 'customer' ? [
    { icon: '1', title: 'Post Clear Job Details', desc: 'Include weight, dimensions, and pickup/delivery requirements' },
    { icon: '2', title: 'Compare Multiple Quotes', desc: 'Wait for a few quotes before accepting to get the best deal' },
    { icon: '3', title: 'Check Provider Ratings', desc: 'Verified providers with good ratings ensure reliable delivery' },
    { icon: '4', title: 'Track Your Delivery', desc: 'Use real-time tracking to know exactly where your freight is' }
  ] : [
    { icon: '1', title: 'Respond Quickly', desc: 'Fast quotes win more jobs - be among the first to respond' },
    { icon: '2', title: 'Be Competitive', desc: 'Check the route distance and set fair prices to win bids' },
    { icon: '3', title: 'Get Verified', desc: 'Verified providers get more visibility and customer trust' },
    { icon: '4', title: 'Update Job Status', desc: 'Keep customers informed with timely status updates' }
  ];

  const handleStartTour = (tourId) => {
    setShowHelpCenter(false);
    setTimeout(() => startTour(tourId), 300);
  };

  return (
    <AnimatePresence>
      {showHelpCenter && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[9990]"
            onClick={() => setShowHelpCenter(false)}
          />

          {/* Help Center Panel */}
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[9991] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <HelpCircle className="w-6 h-6" />
                  </div>
                  <h2 className="text-xl font-bold">Help Center</h2>
                </div>
                <button
                  onClick={() => setShowHelpCenter(false)}
                  className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-white/80 text-sm">
                Learn how to use The Hotshot Marketplace with interactive guides and tips.
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Interactive Tours */}
              <section>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Interactive Guides
                </h3>
                <div className="space-y-2">
                  {availableTours.map((tour) => {
                    const completed = isTourCompleted(tour.id);
                    return (
                      <button
                        key={tour.id}
                        onClick={() => handleStartTour(tour.id)}
                        className="w-full p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 transition-all flex items-center justify-between group"
                      >
                        <div className="flex items-center gap-3">
                          {completed ? (
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                              <PlayCircle className="w-5 h-5 text-orange-600" />
                            </div>
                          )}
                          <div className="text-left">
                            <p className="font-medium text-gray-900">{tour.name}</p>
                            <p className="text-xs text-gray-500">{tour.steps.length} steps</p>
                          </div>
                        </div>
                        {completed && (
                          <span className="text-xs text-green-600 font-medium">Completed</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {availableTours.some(t => isTourCompleted(t.id)) && (
                  <button
                    onClick={resetTours}
                    className="mt-3 text-xs text-gray-500 hover:text-orange-600 transition-colors"
                  >
                    Reset all tour progress
                  </button>
                )}
              </section>

              {/* Quick Tips */}
              <section>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Quick Tips
                </h3>
                <div className="space-y-3">
                  {quickTips.map((tip, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                      <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {tip.icon}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{tip.title}</p>
                        <p className="text-xs text-gray-600">{tip.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ Section */}
              <section>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Common Questions
                </h3>
                <div className="space-y-2">
                  {userRole === 'customer' ? (
                    <>
                      <FaqItem 
                        question="How do I pay for a job?" 
                        answer="Payment is collected securely when you accept a quote. Funds are held until delivery is confirmed, protecting both you and the provider."
                      />
                      <FaqItem 
                        question="What if the delivery is damaged?" 
                        answer="All deliveries are covered by our protection policy. Report any issues within 24 hours and we'll help resolve them."
                      />
                      <FaqItem 
                        question="Can I cancel after accepting a quote?" 
                        answer="Yes, but cancellation fees may apply depending on how close to pickup time you cancel. Contact us for specific situations."
                      />
                    </>
                  ) : (
                    <>
                      <FaqItem 
                        question="When do I get paid?" 
                        answer="Payments are released to your connected bank account within 2-3 business days after the customer confirms delivery."
                      />
                      <FaqItem 
                        question="What's the 10% platform fee?" 
                        answer="A 10% fee is deducted from your quote to cover platform operations, payment processing, and customer support."
                      />
                      <FaqItem 
                        question="How does the free trial work?" 
                        answer="You get 14 days of full access with no subscription charges. After the trial, it's $9.95/week to continue accessing jobs."
                      />
                    </>
                  )}
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Need more help? Contact us at{' '}
                <a href="mailto:support@thehotshotmarketplace.com.au" className="text-orange-600 hover:underline">
                  support@thehotshotmarketplace.com.au
                </a>
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-sm text-gray-900">{question}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-gray-400"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-3 pb-3 text-sm text-gray-600">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HelpCenter;
